// The branded chat page's calls to the public API (docs/branded-page.md
// §Public API). No login: the slug in the address is all there is.

import { replyOf } from './brandPageReply';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.jawabai.cloud';

export function brandImageUrl(id) {
  return id ? `${API_URL}/api/v1/public/brand-images/${encodeURIComponent(id)}` : '';
}

// → { page } | { notFound: true }. Throws on anything else (network, 5xx),
// which the page shows as "try again" — never as "this page does not exist".
export async function fetchBrandPage(slug) {
  const res = await fetch(`${API_URL}/api/v1/public/pages/${encodeURIComponent(slug)}`);
  if (res.status === 404) return { notFound: true };
  if (!res.ok) throw new Error(`page_failed_${res.status}`);
  const json = await res.json();
  return { page: json.data };
}

// Storage can throw (private mode, blocked site data) — every access is
// wrapped, so the worst case is "forgets", never "breaks". Same rule as the
// hosted widget.
function read(store, key) {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function write(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    // ignore
  }
}

function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  // Older browsers without randomUUID: still the cryptographic generator
  // (same as lib/chatApi.js) — the id threads a conversation, so it must not
  // be guessable.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// One conversation per page and visitor: the session id lives in
// localStorage, so the agent remembers the visitor across visits.
function sessionId(slug) {
  const key = `jawabai_page_session:${slug}`;
  let id = read(localStorage, key);
  if (!id) {
    id = newSessionId();
    write(localStorage, key, id);
  }
  return id;
}

// The visible transcript lives in sessionStorage, like the widget: a refresh
// keeps it, tomorrow starts a fresh-looking window.
const TRANSCRIPT_MAX = 60;
export function loadTranscript(slug) {
  try {
    const parsed = JSON.parse(read(sessionStorage, `jawabai_page_chat:${slug}`) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((m) => m && typeof m.text === 'string' && ['me', 'bot'].includes(m.who))
      : [];
  } catch {
    return [];
  }
}
// Suggested-question buttons are not kept: after a reload no reply has them.
export function saveTranscript(slug, messages) {
  const keep = messages
    .filter((m) => m.who !== 'error')
    .slice(-TRANSCRIPT_MAX)
    .map((m) => ({ who: m.who, text: m.text }));
  write(sessionStorage, `jawabai_page_chat:${slug}`, JSON.stringify(keep));
}

// → the reply text. Errors carry a `code` the page turns into a sentence:
// rate_limited (429), gone (404: the page was switched off meanwhile),
// failed (anything else).
export async function sendBrandPageMessage(slug, content) {
  let res;
  try {
    res = await fetch(`${API_URL}/api/v1/public/pages/${encodeURIComponent(slug)}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, sessionId: sessionId(slug) }),
    });
  } catch {
    throw Object.assign(new Error('offline'), { code: 'offline' });
  }
  if (res.status === 429) throw Object.assign(new Error('rate_limited'), { code: 'rate_limited' });
  if (res.status === 404) throw Object.assign(new Error('gone'), { code: 'gone' });
  if (!res.ok)
    throw Object.assign(new Error(`chat_failed_${res.status}`), {
      code: 'failed',
    });
  const json = await res.json();
  return replyOf(json);
}

// "Report this page". Throws with a `code` like sendBrandPageMessage:
// rate_limited (429), gone (404), offline, failed.
export async function reportBrandPage(slug, reason, details) {
  let res;
  try {
    res = await fetch(`${API_URL}/api/v1/public/pages/${encodeURIComponent(slug)}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(details ? { reason, details } : { reason }),
    });
  } catch {
    throw Object.assign(new Error('offline'), { code: 'offline' });
  }
  if (res.status === 429) throw Object.assign(new Error('rate_limited'), { code: 'rate_limited' });
  if (res.status === 404) throw Object.assign(new Error('gone'), { code: 'gone' });
  if (!res.ok) throw Object.assign(new Error(`report_failed_${res.status}`), { code: 'failed' });
}
